import React from 'react';
import { Card, Text, Stack, Badge, Group, Code, Divider } from '@mantine/core';
import { useAuth, usePermissions } from '../../hooks/useAuth';

/**
 * Debug panel to help diagnose authentication and role loading issues
 * Only shows in development mode
 */
export const AuthDebugPanel: React.FC = () => {
  const { user, isAuthenticated, isLoading, error } = useAuth();
  const { currentRole, roleLabel, hasAnyRole } = usePermissions();

  // Only show in development
  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <Card 
      withBorder 
      style={{ 
        position: 'fixed', 
        bottom: '10px', 
        right: '10px', 
        zIndex: 1000,
        maxWidth: '300px',
        fontSize: '12px'
      }}
    >
      <Text size="xs" fw={600} mb="xs">Auth Debug Panel</Text>
      
      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="xs">Authenticated:</Text>
          <Badge color={isAuthenticated ? 'green' : 'red'} size="xs">
            {isAuthenticated ? 'Yes' : 'No'}
          </Badge>
        </Group>

        <Group justify="space-between">
          <Text size="xs">Loading:</Text>
          <Badge color={isLoading ? 'yellow' : 'gray'} size="xs">
            {isLoading ? 'Yes' : 'No'}
          </Badge>
        </Group>

        {error && (
          <Group justify="space-between">
            <Text size="xs">Error:</Text>
            <Text size="xs" c="red">{error}</Text>
          </Group>
        )}

        <Divider />

        {user && (
          <>
            <Group justify="space-between">
              <Text size="xs">Email:</Text>
              <Text size="xs">{user.email}</Text>
            </Group>

            <Group justify="space-between">
              <Text size="xs">Role (direct):</Text>
              <Badge size="xs">{user.role || 'None'}</Badge>
            </Group>

            <Group justify="space-between">
              <Text size="xs">Role Info:</Text>
              <Badge size="xs">{user.role_info?.value || 'None'}</Badge>
            </Group>

            <Group justify="space-between">
              <Text size="xs">Current Role:</Text>
              <Badge size="xs">{currentRole || 'None'}</Badge>
            </Group>

            <Group justify="space-between">
              <Text size="xs">Role Label:</Text>
              <Text size="xs">{roleLabel || 'None'}</Text>
            </Group>

            <Divider />

            <Text size="xs" fw={500}>Role Checks:</Text>
            <Group justify="space-between">
              <Text size="xs">Is Admin:</Text>
              <Badge color={hasAnyRole(['admin', 'super_admin']) ? 'green' : 'red'} size="xs">
                {hasAnyRole(['admin', 'super_admin']) ? 'Yes' : 'No'}
              </Badge>
            </Group>

            <Group justify="space-between">
              <Text size="xs">Is Parent:</Text>
              <Badge color={hasAnyRole(['parent']) ? 'green' : 'red'} size="xs">
                {hasAnyRole(['parent']) ? 'Yes' : 'No'}
              </Badge>
            </Group>

            <Group justify="space-between">
              <Text size="xs">Is Viewer:</Text>
              <Badge color={hasAnyRole(['viewer']) ? 'green' : 'red'} size="xs">
                {hasAnyRole(['viewer']) ? 'Yes' : 'No'}
              </Badge>
            </Group>

            <Divider />

            <Text size="xs" fw={500}>Raw User Object:</Text>
            <Code block style={{ fontSize: '10px', maxHeight: '100px', overflow: 'auto' }}>
              {JSON.stringify(user, null, 2)}
            </Code>
          </>
        )}

        {!user && isAuthenticated && (
          <Text size="xs" c="red">
            Authenticated but no user data!
          </Text>
        )}
      </Stack>
    </Card>
  );
};

export default AuthDebugPanel;
